import { Log } from '../AuxillaryService/Log';
import { Signage } from '../Signage';
import { ReachAPI } from '../ReachApi';
import { ActivePromises } from '../Utility/ActivePromises';
import { glom } from '../Signage/glom';
import { LocalCache } from './LocalCache';
import { Asset } from './Asset';
import { ImageCache } from './ImageCache';
import { $ } from '../../vendor/jquery';
import _ from 'underscore';

/**
 * Failure Response
 *
 * Returns 4 values:
 *
 * 1. {XMLHttpRequest} xhr
 * 2. {string} textStatus
 * 3. {string} msg
 * 4. {Event} event
 * @typedef {jQuery.Promise} jqpFailureFontCache
 */

/**
 * @class
 * @hideconstructor
 */
const FontCache = {};

/**
 * @type {MutationObserver}
 * @private
 * @memberof FontCache
 */
var _observer;

/**
 * Track active image retrievals (to prevent duplicate downloads of the same
 * font before it's cached)
 * @type {ActivePromises}
 * @protected
 * @inner
 * @memberof FontCache
 */
var _activePromises = new ActivePromises('url');

/**
 * @typedef {object} FontItemHash
 * @property {string} family - Font Family Name
 * @property {Array.<string>} aliases - Alias Font Family Names
 * @property {boolean} hidden
 * @property {boolean} private
 * @property {Element} styleElement - style element containing CSS
 * @property {Array.<Object>} sources
 * @property {string} sources[].basename - basename of font file
 * @property {string} sources[].url - url to font file
 * @property {('normal'|'bold'|'italic'|'bolditalic')} sources[].type - Font
 * type 'normal', 'bold', or 'italic'
 */

/**
 * Base URL
 * @type {string}
 * @protected
 */
FontCache._baseUrl = FontCache._baseUrl || process.env.BASE_URL_SIGNS;

/**
 * List of Font objects
 * @type {Array.<FontItemHash>}
 * @protected
 */
FontCache._fontList = [];

/**
 * Total number of stylesheets in document
 * @type {number}
 * @protected
 */
FontCache._totalStylesheets = 0;

/**
 * List of Font names being used in signage
 * @type {Array.<string>}
 * @protected
 */
FontCache._fontNames = [];

/**
 * Cached Font Call with Fallbacks
 * @param {string} url
 * @throws {jqpFailureFontCache}
 * @returns {jQuery.Promise<string, string>}
 *      Success `jQuery.Promise[{string} url, {string} textStatus]`
 */
FontCache.getCached = function (url) {
    var activePromiseItem = { url: url };

    var currentlyActiveItem = _activePromises.get(activePromiseItem);
    if (currentlyActiveItem) {
        return currentlyActiveItem.promise;
    }

    var jqDeferred = $.Deferred();
    var jqPromise = jqDeferred.promise();

    activePromiseItem.promise = jqPromise;
    _activePromises.add(activePromiseItem);

    if (glom.caching === false) {
        jqDeferred.resolve(url, 'noCache');
    } else {
        // Get cached asset via `LocalCache`

        LocalCache.get(url)
            .done(function (blob) {
                // got data from cache
                var dataURL = URL.createObjectURL(blob);
                jqDeferred.resolve(dataURL, 'cache');
            })
            .fail(function (msg, event) {
                // no data in cache

                Asset.retrieve(url)
                    .done(function (blob, statusText, xhr) {
                        // Put the received blob into IndexedDB
                        LocalCache.save(url, blob)
                            .done(function () {
                                var dataURL = URL.createObjectURL(blob);
                                jqDeferred.resolve(dataURL, 'cache');
                            })
                            .fail(function (msg, event) {
                                // fail
                                jqDeferred.reject(xhr, xhr.statusText, msg, event);
                            });
                    })
                    .fail(function (xhr, statusText) {
                        jqDeferred.reject(xhr, statusText, msg, event);
                    });
            });
    }

    return jqPromise;
};

/**
 * Start DOM mutation observer for Fonts
 * @param {Element} [target]
 * @param {object} [config]
 * @returns {Promise}
 */
FontCache.startObserver = function (target, config) {
    // step 1 - get allowed fonts from the API
    return _populateFontList().then(function () {
        target = target || document.body;
        config = config || {
            attributes: true,
            attributeFilter: ['style'],
            childList: true,
            characterData: false,
            subtree: true,
        };

        // configure MutationObserver to find fonts added to the DOM
        _observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (
                    mutation.target.querySelector('style') !== null ||
                    !FontCache._totalStylesheets ||
                    document.styleSheets.length > FontCache._totalStylesheets
                ) {
                    FontCache._totalStylesheets = document.styleSheets.length;
                    _findStylesSheets().forEach(function (fontfamily) {
                        FontCache.addUniqueFont(fontfamily);
                    });
                }

                _findInlineFonts(mutation).forEach(function (fontfamily) {
                    FontCache.addUniqueFont(fontfamily);
                });
            });
        });

        // step 2 - find fonts already being used in the DOM
        FontCache._totalStylesheets = document.styleSheets.length;
        _scanDomForFonts().forEach(function (fontfamily) {
            FontCache.addUniqueFont(fontfamily);
        });

        // step 3 - start MutationObserver to find fonts as they are
        //          added to the DOM
        _observer.observe(target, config);
    });
};

/**
 * Add font to list to ensure no repeat fonts
 * @param {string} fontFamily - Can be a single font, or a list of fonts
 * separated by commas.
 * @todo This function will not properly add fonts with commas(,) in their
 * names.  We currently do not have any such fonts, but this function may
 * need to be updated if that changes.
 */
FontCache.addUniqueFont = function (fontFamily) {
    fontFamily = fontFamily.replace(/["']/g, '');
    var fontFamilies = fontFamily.split(',');
    if (fontFamilies && fontFamilies.length) {
        fontFamilies.forEach(function (font) {
            var trimmedFont = font.replace(/^\s+|\s+$/gm, '');
            if ($.inArray(trimmedFont, FontCache._fontNames) === -1) {
                FontCache._fontNames.push(trimmedFont);
                FontCache.addFontToSignage(trimmedFont);
            }
        });
    }
};

/**
 * Find inline-styled fonts
 * @param {MutationRecord} mutation
 * @returns {Array.<string>}
 * @private
 */
var _findInlineFonts = function (mutation) {
    var inlineFontFamilies = [];

    if (mutation.type === 'attributes') {
        if (mutation.target.style.fontFamily) {
            inlineFontFamilies.push(mutation.target.style.fontFamily);
        }
    } else if (mutation.type === 'childList') {
        if (mutation.target.style.fontFamily) {
            inlineFontFamilies.push(mutation.target.style.fontFamily);
        }
        _.map(mutation.target.querySelectorAll('*'), function (child) {
            if (child.style.fontFamily) {
                inlineFontFamilies.push(child.style.fontFamily);
            }
        });
    }

    return inlineFontFamilies;
};

/**
 * Find style sheets in document
 * @returns {Array.<string>}
 * @private
 */
var _findStylesSheets = function () {
    var fontFamilies = [];
    var styleTags = document.styleSheets;

    if (styleTags) {
        for (var x = 0; x < styleTags.length; x++) {
            var cssRules;
            try {
                cssRules = styleTags[x].cssRules;
            } catch (cssRulesError) {
                if (
                    cssRulesError instanceof DOMException &&
                    cssRulesError.name === 'SecurityError'
                ) {
                    // This error is thrown when trying to read CSS files from
                    // another domain, so in this instance we'll return no CSS
                    // rules.
                    cssRules = [];
                } else {
                    throw cssRulesError;
                }
            }

            if (cssRules.length) {
                for (var z = 0; z < cssRules.length; z++) {
                    if (
                        cssRules[z].style &&
                        cssRules[z].style.fontFamily &&
                        $.inArray(cssRules[z].style.fontFamily, fontFamilies) === -1
                    ) {
                        fontFamilies.push(cssRules[z].style.fontFamily);
                    }
                }
            }
        }
    }

    return fontFamilies;
};

/**
 * Find fonts currently being used, by searching attached stylesheets and
 * inline styles.
 * @returns {Array.<string>} List of `font-family` CSS property values
 * @private
 */
var _scanDomForFonts = function () {
    // This dummy MutationRecord tricks `_findInlineFonts()` into scanning
    // every child element of the `<body>` tag for fonts in their inline
    // styles.
    var dummyBodyMutationRecord = {
        type: 'childList',
        target: document.body,
        addedNodes: document.body.childNodes,
        removedNodes: [],
        previousSibling: null,
        nextSibling: null,
        attributeName: null,
        attributeNamespace: null,
        oldValue: null,
    };

    var styleSheetFontFamilies = _findStylesSheets();
    var inlineFontFamilies = _findInlineFonts(dummyBodyMutationRecord);

    return styleSheetFontFamilies.concat(inlineFontFamilies);
};

/**
 * Populate the internal list of fonts available for use.
 * @returns {Promise}
 * @private
 */
var _populateFontList = function () {
    return Promise.resolve()
        .then(
            function () {
                if (Signage.playerUuid) {
                    return ReachAPI.font.getListPlayer(Signage.playerUuid, true);
                } else {
                    return ReachAPI.font.getList(true, true);
                }
            }.bind(this)
        )
        .then(
            function (allowedFontList) {
                allowedFontList.fonts.forEach(function (font) {
                    /**
                     * @type FontItemHash
                     */
                    var fontItem = {
                        family: font.family_name,
                        aliases:
                            font.family_aliases && font.family_aliases.length >= 1
                                ? font.family_aliases
                                : [],
                        sources: [],
                        styleElement: null,
                        hidden: font.is_hidden === true,
                        private: font.is_private === true,
                    };

                    if (font.file_name.regular) {
                        fontItem.sources.push({
                            basename: font.file_name.regular,
                            url: 'fonts/' + font.file_name.regular,
                            type: 'normal',
                        });
                    }

                    if (font.file_name.bold) {
                        fontItem.sources.push({
                            basename: font.file_name.bold,
                            url: 'fonts/' + font.file_name.bold,
                            type: 'bold',
                        });
                    }

                    if (font.file_name.italic) {
                        fontItem.sources.push({
                            basename: font.file_name.italic,
                            url: 'fonts/' + font.file_name.italic,
                            type: 'italic',
                        });
                    }

                    if (font.file_name.bold_italic) {
                        fontItem.sources.push({
                            basename: font.file_name.bold_italic,
                            url: 'fonts/' + font.file_name.bold_italic,
                            type: 'bolditalic',
                        });
                    }

                    FontCache._fontList.push(fontItem);
                });
            }.bind(this)
        );
};

/**
 * Stop DOM mutation observer for Fonts
 */
FontCache.stopObserver = function () {
    _observer.disconnect();
};

/**
 * Add font style tag to header tag
 * @param {string} [findFont]
 */
FontCache.addFontToSignage = function (findFont) {
    FontCache._fontList.forEach(function (font) {
        var fontFound = null;
        if (font.family === findFont) {
            fontFound = font;
        } else {
            font.aliases.forEach(function (alias) {
                if (alias === findFont) {
                    fontFound = font;
                }
            });
        }

        if (fontFound && fontFound.styleElement === null) {
            var familyNames = [].concat(fontFound.family, fontFound.aliases);
            var styleElement = document.createElement('style');
            styleElement.classList.add('fontcached');
            styleElement.dataset.fontFamily = fontFound.family;
            document.head.appendChild(styleElement);
            fontFound.styleElement = styleElement;

            familyNames.forEach(function (fontFamily) {
                fontFound.sources.forEach(function (fontSrc) {
                    var cachedFontPromise = FontCache.getCached(
                        FontCache._baseUrl + '/' + fontSrc.url
                    );

                    cachedFontPromise.done(function (cachedURL) {
                        var tags = fontFound.aliases.indexOf(fontFamily) !== -1 ? '[ALIAS] ' : '';
                        var cssText =
                            '/* ' +
                            tags +
                            fontFamily +
                            ' (' +
                            fontSrc.basename +
                            ') */ \n' +
                            FontCache._cssText(fontFamily, cachedURL, fontSrc.type);
                        var styleTextNode = document.createTextNode(cssText);

                        Log.Debug(
                            'Retrieved Font `' +
                                fontFamily +
                                (fontSrc.type !== 'normal'
                                    ? ' ' + fontSrc.type.toUpperCase()
                                    : '') +
                                '`',
                            null,
                            __fileline
                        );

                        styleElement.appendChild(styleTextNode);
                    });

                    cachedFontPromise.fail(function (xhr, statusText, msg, event) {
                        Log.Warning(
                            'Failed to get cached font: ' +
                                fontFamily +
                                ' (' +
                                fontSrc.basename +
                                ')',
                            null,
                            __fileline,
                            ImageCache.createPromiseFailObject(xhr, statusText, msg, event)
                        );
                    });
                });
            });
        }
    });
};

/**
 * Font Face CSS
 * @param {string} family
 * @param {string} url
 * @param {string} type
 * @return {string}
 * @protected
 */
FontCache._cssText = function (family, url, type) {
    type = type || 'normal';
    var cssText = '';

    cssText += '@font-face { \n';
    cssText += "    font-family: '" + family + "'; \n";
    cssText += '    src: url("' + url + '"); \n';
    if (type === 'bold') {
        cssText += '    font-weight: bold; \n';
    } else if (type === 'italic') {
        cssText += '    font-style: italic; \n';
    } else if (type === 'bolditalic') {
        cssText += '    font-weight: bold; \n';
        cssText += '    font-style: italic; \n';
    }
    cssText += '} \n';

    return cssText;
};

export { FontCache };
